// phraseSlotSync.ts
import type { Editor } from '@tiptap/core';

/**
 * Reading and writing the report's phrase slots — the `data-editable` spans a clinician
 * clicks to pick a word from the clinic's list.
 *
 * Same shape as `chainFieldSync.ts`, against the older `editableWord` Mark instead of the
 * chain Mark. Kept separate rather than generalised: the chain owns measured values and
 * writes them freely, whereas a phrase is a clinician's finding and is only ever written
 * under the conditions in `applyPhraseSuggestions` below.
 */

export type PhraseOccurrence = {
  /** The span's first class — which sentence this is. */
  slot: string;
  from: number;
  to: number;
  text: string;
  /** `data-user` — a clinician chose these words. The engine must never write here. */
  userOwned: boolean;
  /** `data-auto` — the exact text the engine last wrote here, or null if it never has. */
  autoText: string | null;
  /**
   * The first few words after the slot, up to the end of its sentence. Two spans can
   * carry the same class and describe different findings — the Aortic Valve line prints
   * `la-no` twice, once before "aortic regurgitation" and once before "aortic stenosis" —
   * and picking a word for one must not answer for the other. What follows the slot is
   * what tells them apart.
   */
  context: string;
};

/**
 * Does this position continue a sentence that has just ended?
 *
 * Needed because one slot can print in two grammatical positions. The arrhythmia phrase
 * sits mid-sentence in the Findings ("The following arrhythmias were noted: no
 * arrhythmias") and starts its own sentence in the Conclusions ("...myocardial ischemia.
 * No arrhythmias.") — Nikhil, 02 Sep, wanted the Conclusions copy reading exactly "No
 * arrhythmias." while the two lines still move together. They share a class, so they
 * share one stored phrase, and the case has to come from the position rather than the
 * value.
 *
 * Deliberately narrow: ONLY a preceding `.`, `?` or `!` in the same block counts.
 *
 *   - a colon does not — "noted: no arrhythmias" is how the Findings line has always read
 *   - the START of a block does not either, even though a slot opening a paragraph is
 *     grammatically sentence-initial. Plenty of existing templates begin a cell or a
 *     bullet with a slot, and capitalising those would rewrite wording across every
 *     clinic's reports for a change nobody asked for. Their behaviour is unchanged.
 */
const startsSentence = (doc: any, from: number): boolean => {
  try {
    const $pos = doc.resolve(from);
    const before = $pos.parent.textBetween(0, $pos.parentOffset).replace(/\s+$/, '');
    return /[.?!]$/.test(before);
  } catch {
    // A position we cannot resolve is not worth guessing about; leave the case alone.
    return false;
  }
};

/** How many words after a slot are compared. Enough to separate two findings. */
const CONTEXT_WORDS = 4;

/** The words a slot qualifies: what follows it, to the end of the sentence. */
const phraseContext = (doc: any, to: number): string => {
  try {
    const $pos = doc.resolve(to);
    const after = $pos.parent.textBetween($pos.parentOffset, $pos.parent.content.size);
    return after
      .split(/[.?!]/)[0]
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .filter(Boolean)
      .slice(0, CONTEXT_WORDS)
      .join(' ');
  } catch {
    // Unresolvable position: treat as no context, so it groups with the other blanks
    // rather than silently becoming a slot of its own.
    return '';
  }
};

const capitalizeFirst = (text: string): string =>
  text.length === 0 ? text : text[0].toUpperCase() + text.slice(1);

const slotFromClass = (className: unknown): string | null => {
  if (typeof className !== 'string') {
    return null;
  }
  const first = className.trim().split(/\s+/)[0];
  return first || null;
};

export function findPhraseOccurrences(editor: Editor): PhraseOccurrence[] {
  const found: PhraseOccurrence[] = [];
  if (!editor) {
    return found;
  }

  editor.state.doc.descendants((node, pos) => {
    if (!node.isText || !node.text) {
      return;
    }
    const mark = node.marks.find(m => m.type.name === 'editableWord');
    if (!mark) {
      return;
    }
    const slot = slotFromClass(mark.attrs.class);
    if (!slot) {
      return;
    }
    found.push({
      slot,
      from: pos,
      to: pos + node.nodeSize,
      text: node.text,
      userOwned: String(mark.attrs['data-user'] || '') === '1',
      autoText:
        typeof mark.attrs['data-auto'] === 'string' ? (mark.attrs['data-auto'] as string) : null,
      context: phraseContext(editor.state.doc, pos + node.nodeSize),
    });
  });

  return found;
}

/** Occurrences grouped by slot, so ownership can be judged across every copy at once. */
export function groupPhraseOccurrences(editor: Editor): Record<string, PhraseOccurrence[]> {
  const grouped: Record<string, PhraseOccurrence[]> = {};
  findPhraseOccurrences(editor).forEach(occurrence => {
    (grouped[occurrence.slot] = grouped[occurrence.slot] || []).push(occurrence);
  });
  return grouped;
}

/** Snapshot of `slot -> current phrase`, for change detection. */
export function collectPhraseSnapshot(editor: Editor): Record<string, string> {
  const values: Record<string, string> = {};
  findPhraseOccurrences(editor).forEach(({ slot, text }) => {
    values[slot] = text;
  });
  return values;
}

/**
 * Is this slot the engine's to write, or a clinician's?
 *
 * Locked when ANY occurrence says so, which is deliberately the conservative reading: the
 * copies of one sentence are meant to agree, so if a human has taken over the copy in the
 * Conclusions, the engine must not keep rewriting the copy in the Findings and leave the
 * two contradicting each other.
 *
 * Three ways a slot becomes the clinician's, all of them durable because both attributes
 * are saved with the report:
 *
 *   1. `data-user="1"` — they picked from the dropdown. Permanent.
 *   2. the text no longer matches `data-auto` — they typed over what the engine wrote.
 *   3. `data-auto` is set on one occurrence and absent on another whose text differs —
 *      the copies have been edited apart by hand.
 *
 * A slot with no `data-auto` anywhere is an untouched template default and is free. That
 * inference holds because the engine runs when the report opens, before anyone can type.
 */
export function isPhraseSlotLocked(occurrences: PhraseOccurrence[]): boolean {
  if (occurrences.length === 0) {
    return true; // nothing to write to
  }
  return occurrences.some(occurrence => {
    if (occurrence.userOwned) {
      return true;
    }
    if (occurrence.autoText === null) {
      return false; // never written by the engine — a template default
    }
    return occurrence.autoText !== occurrence.text;
  });
}


/**
 * The occurrences a clinician's pick should reach.
 *
 * With no origin, every copy of the slot — how the wording engine writes, and how a pick
 * behaved before. With an origin, only the copies that qualify the same words, so a pick
 * on "No aortic regurgitation" leaves "No aortic stenosis" alone even though both spans
 * carry `la-no`.
 */
const sameFinding = (occurrences: PhraseOccurrence[], origin?: number): PhraseOccurrence[] => {
  if (typeof origin !== 'number') {
    return occurrences;
  }
  const clicked = occurrences.find(o => o.from <= origin && origin < o.to);
  if (!clicked) {
    return occurrences; // origin outside any slot: nothing to narrow by
  }
  return occurrences.filter(o => o.context === clicked.context);
};

/**
 * Record that a clinician chose the words in this slot, so the engine leaves it alone for
 * good — including on every future open of the report.
 *
 * Writes `data-user="1"` onto every occurrence of the slot and clears `data-auto`, which
 * matters: a stale `data-auto` equal to the new text would otherwise read as "the engine
 * wrote this" and hand the slot back to the engine.
 *
 * Returns how many occurrences were marked.
 */
export function markPhraseUserOwned(editor: Editor, slot: string, origin?: number): number {
  if (!editor || !slot) {
    return 0;
  }
  const { state } = editor;
  const markType = state.schema.marks.editableWord;
  if (!markType) {
    return 0;
  }

  const occurrences = sameFinding(
    findPhraseOccurrences(editor).filter(o => o.slot === slot),
    origin
  );
  if (occurrences.length === 0) {
    return 0;
  }

  const { tr } = state;
  let marked = 0;
  // Back-to-front, as everywhere else here, so an earlier position is still valid after a
  // later one has been rewritten.
  for (let i = occurrences.length - 1; i >= 0; i -= 1) {
    const occurrence = occurrences[i];
    const node = state.doc.nodeAt(occurrence.from);
    if (!node || !node.isText) {
      continue;
    }
    const existing = node.marks.find(m => m.type.name === 'editableWord');
    if (!existing) {
      continue;
    }
    if (String(existing.attrs['data-user'] || '') === '1' && !existing.attrs['data-auto']) {
      continue; // already recorded
    }
    const nextMarks = node.marks.map(m =>
      m.type.name === 'editableWord'
        ? markType.create({ ...m.attrs, 'data-user': '1', 'data-auto': null })
        : m
    );
    tr.replaceWith(occurrence.from, occurrence.to, state.schema.text(occurrence.text, nextMarks));
    marked += 1;
  }

  if (marked > 0) {
    editor.view.dispatch(tr);
  }
  return marked;
}

/**
 * Write phrases into their slots, in one transaction, preserving every mark on the text.
 *
 * Back-to-front so an earlier occurrence's positions survive a later rewrite of a
 * different length. Returns how many were written.
 *
 * `byEngine` stamps `data-auto` with exactly what was written, so a later open can tell
 * the engine's own words from a clinician's. It is false for a human's dropdown pick,
 * which is propagated through here too but must not be claimed by the engine.
 */
export function applyPhraseUpdates(
  editor: Editor,
  updates: Record<string, string>,
  byEngine = false,
  origin?: number
): number {
  if (!editor || !updates || Object.keys(updates).length === 0) {
    return 0;
  }

  const { state } = editor;
  const { tr } = state;
  const wordMarkType = state.schema.marks.editableWord;
  const occurrences = sameFinding(
    findPhraseOccurrences(editor).filter(o =>
      Object.prototype.hasOwnProperty.call(updates, o.slot)
    ),
    origin
  );
  let written = 0;

  for (let i = occurrences.length - 1; i >= 0; i -= 1) {
    const occurrence = occurrences[i];
    const raw = updates[occurrence.slot];
    if (!raw) {
      continue;
    }
    // Cased for THIS occurrence's position, and cased BEFORE the no-change guard rather
    // than after it. Comparing the raw lowercase value against an already-capitalised
    // slot never matches, so the guard would let a write through on every pass — an
    // endless rewrite of an unchanged sentence, dirtying the document and triggering
    // autosave on a report nobody had touched.
    const next = startsSentence(state.doc, occurrence.from) ? capitalizeFirst(raw) : raw;
    if (next === occurrence.text) {
      continue;
    }
    const node = state.doc.nodeAt(occurrence.from);
    if (!node || !node.isText) {
      continue;
    }
    // `data-auto` records the engine's own words, cased for THIS occurrence, so a future
    // open can tell an untouched engine write from a value a clinician has since changed.
    const marks =
      byEngine && wordMarkType
        ? node.marks.map(m =>
            m.type.name === 'editableWord'
              ? wordMarkType.create({ ...m.attrs, 'data-auto': next })
              : m
          )
        : node.marks;
    tr.replaceWith(occurrence.from, occurrence.to, state.schema.text(next, marks));
    written += 1;
  }

  if (written > 0) {
    editor.view.dispatch(tr);
  }

  return written;
}
